import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import Footer from "components/Footer/Footer";
import SEO from "components/Common/SEO";
import { getPageTitle } from "lib/legacy";
import "./ApiReferencePage.css";

// ─── Section registry ─────────────────────────────────────────────────────────

type Method = "GET" | "POST" | "DELETE" | "WS";

type Param = {
  name: string;
  type: string;
  required?: boolean;
  desc: string;
};

type Endpoint = {
  id: string;
  method: Method;
  path: string;
  title: string;
  desc: string;
  params?: Param[];
  bodyParams?: Param[];
  curlExample?: string;
  responseExample?: string;
  auth?: boolean;
};

type Section = {
  id: string;
  label: string;
  icon: string;
  desc: string;
  endpoints: Endpoint[];
};

const BASE = "https://api.moneyxpro.com";

const SECTIONS: Section[] = [
  // ── Getting Started ────────────────────────────────────────────────────────
  {
    id: "getting-started",
    label: "Getting Started",
    icon: "🚀",
    desc: "Base URL, authentication, and conventions",
    endpoints: [],
  },

  // ── Prices ─────────────────────────────────────────────────────────────────
  {
    id: "prices",
    label: "Prices",
    icon: "💹",
    desc: "Live prices and historical OHLCV candles",
    endpoints: [
      {
        id: "prices-live",
        method: "GET",
        path: "/api/prices/live",
        title: "Live Prices",
        desc: "Returns the latest oracle price for every tracked token, updated every second from the Binance feed.",
        curlExample: `curl ${BASE}/api/prices/live`,
        responseExample: `{
  "BTC": 67842.50,
  "ETH": 3512.10,
  "BNB": 578.90,
  "SOL": 142.30,
  "BESC": 0.0842,
  "AAPL": 214.70,
  "TSLA": 248.90,
  "NVDA": 882.40
}`,
      },
      {
        id: "prices-tickers",
        method: "GET",
        path: "/api/prices/tickers",
        title: "24h Tickers",
        desc: "Returns 24-hour price change, high, low, and volume for all tracked tokens.",
        curlExample: `curl ${BASE}/api/prices/tickers`,
        responseExample: `[
  {
    "symbol": "BTC",
    "price": 67842.50,
    "change24h": 2.34,
    "high24h": 68500.00,
    "low24h": 66100.00,
    "volume24h": 28500000000
  }
]`,
      },
      {
        id: "prices-candles-token",
        method: "GET",
        path: "/api/prices/candles/:token",
        title: "OHLCV Candles — Crypto",
        desc: "Historical OHLCV candles for a crypto token, built from on-chain execution prices.",
        params: [
          { name: "token",      type: "string (path)", required: true,  desc: "Token contract address (lowercase)" },
          { name: "resolution", type: "string",        required: false, desc: "Candle resolution: 1, 5, 15, 30, 60, 240, D (default: 60)" },
          { name: "from",       type: "unix timestamp",required: false, desc: "Start timestamp (default: 7 days ago)" },
          { name: "to",         type: "unix timestamp",required: false, desc: "End timestamp (default: now)" },
        ],
        curlExample: `curl "${BASE}/api/prices/candles/0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c?resolution=60&from=1700000000"`,
        responseExample: `[
  {
    "time": 1700035200,
    "open": 37100.00,
    "high": 37450.00,
    "low": 36980.00,
    "close": 37350.00,
    "volume": 48200000
  }
]`,
      },
      {
        id: "prices-candles-stock",
        method: "GET",
        path: "/api/prices/candles",
        title: "OHLCV Candles — Stocks",
        desc: "Historical OHLCV candles for synthetic stock/commodity tokens via Yahoo Finance.",
        params: [
          { name: "tokenSymbol", type: "string", required: true,  desc: "Symbol: AAPL, TSLA, NVDA, MSFT, GOOGL, AMZN, META, SPY, QQQ, GOLD" },
          { name: "period",      type: "string", required: false, desc: "Resolution: 5m, 15m, 1h, 4h, 1d (default: 1h)" },
          { name: "limit",       type: "number", required: false, desc: "Max candles to return (default: 200, max: 5000)" },
        ],
        curlExample: `curl "${BASE}/api/prices/candles?tokenSymbol=AAPL&period=1h&limit=50"`,
        responseExample: `{
  "candles": [
    [1700035200, 174.50, 175.80, 174.10, 175.40],
    [1700031600, 173.90, 174.60, 173.50, 174.50]
  ]
}`,
      },
      {
        id: "prices-last",
        method: "GET",
        path: "/api/prices/last/:token",
        title: "Last Price",
        desc: "Returns the last recorded oracle price for a single token address.",
        params: [
          { name: "token", type: "string (path)", required: true, desc: "Token contract address (lowercase)" },
        ],
        curlExample: `curl ${BASE}/api/prices/last/0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c`,
        responseExample: `{
  "token": "0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c",
  "price": "67842500000000000000000000000000000",
  "priceUsd": 67842.50,
  "timestamp": 1700035248
}`,
      },
      {
        id: "prices-24h",
        method: "GET",
        path: "/api/prices/24h",
        title: "24h Stats",
        desc: "24-hour open, close, high, low, and volume for a given token based on on-chain trade data.",
        params: [
          { name: "token", type: "string", required: false, desc: "Filter to a single token address. Omit to return all." },
        ],
        curlExample: `curl "${BASE}/api/prices/24h?token=0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c"`,
        responseExample: `[
  {
    "token": "0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c",
    "open": 66200.00,
    "close": 67842.50,
    "high": 68500.00,
    "low": 66000.00,
    "volume": 48200000000
  }
]`,
      },
    ],
  },

  // ── WebSocket ──────────────────────────────────────────────────────────────
  {
    id: "websocket",
    label: "WebSocket",
    icon: "⚡",
    desc: "Sub-second live price feed via WebSocket",
    endpoints: [
      {
        id: "ws-prices",
        method: "WS",
        path: "/ws/prices",
        title: "Live Price Stream",
        desc: `Connect to this WebSocket endpoint to receive real-time price updates. On connect you receive a full snapshot of all prices, then tick updates as individual prices change. The feed re-broadcasts Binance miniTicker data.`,
        curlExample: `# Using wscat
wscat -c wss://api.moneyxpro.com/ws/prices`,
        responseExample: `// On connect — full snapshot
{
  "type": "snapshot",
  "data": {
    "BTC": 67842.50,
    "ETH": 3512.10,
    "BNB": 578.90,
    "AAPL": 214.70
  }
}

// On each tick — single symbol update
{
  "type": "tick",
  "data": {
    "symbol": "BTC",
    "price": 67851.20,
    "ts": 1700035248123
  }
}`,
      },
    ],
  },

  // ── Trades ─────────────────────────────────────────────────────────────────
  {
    id: "trades",
    label: "Trades",
    icon: "📊",
    desc: "Historical trade and swap records",
    endpoints: [
      {
        id: "trades-list",
        method: "GET",
        path: "/api/trades",
        title: "List Trades",
        desc: "Returns paginated trade history. Filter by account, market, action type, or time range.",
        params: [
          { name: "account",    type: "string",  required: false, desc: "Filter by trader address (lowercase)" },
          { name: "indexToken", type: "string",  required: false, desc: "Filter by market token address" },
          { name: "action",     type: "string",  required: false, desc: "increase | decrease | liquidate | swap" },
          { name: "from",       type: "unix ts", required: false, desc: "Start timestamp filter" },
          { name: "to",         type: "unix ts", required: false, desc: "End timestamp filter" },
          { name: "page",       type: "number",  required: false, desc: "Page number (default: 1)" },
          { name: "pageSize",   type: "number",  required: false, desc: "Results per page (default: 50, max: 200)" },
        ],
        curlExample: `curl "${BASE}/api/trades?account=0xYOUR_ADDRESS&pageSize=20"`,
        responseExample: `{
  "total": 142,
  "page": 1,
  "pageSize": 20,
  "items": [
    {
      "id": 18492,
      "tx_hash": "0xabc...def",
      "account": "0x1234...abcd",
      "action": "increase",
      "index_token": "0x7130...d9c",
      "collateral_token": "0xb54a...a4b",
      "size_delta": "50000000000000000000000000000000000",
      "collateral_delta": "1000000000000000000000000000000000",
      "is_long": true,
      "price": "67842500000000000000000000000000000",
      "fee": "50000000000000000000000000000000",
      "realised_pnl": null,
      "block_ts": 1700035248,
      "block_number": 4182920
    }
  ]
}`,
      },
      {
        id: "trades-first",
        method: "GET",
        path: "/api/trades/first",
        title: "Has Traded",
        desc: "Returns whether an address has placed at least one trade. Used to show first-time user prompts.",
        params: [
          { name: "account", type: "string", required: true, desc: "Trader address (lowercase)" },
        ],
        curlExample: `curl "${BASE}/api/trades/first?account=0xYOUR_ADDRESS"`,
        responseExample: `{ "hasTraded": true }`,
      },
      {
        id: "trades-swaps",
        method: "GET",
        path: "/api/trades/swaps",
        title: "List Swaps",
        desc: "Returns paginated token swap records.",
        params: [
          { name: "account",  type: "string",  required: false, desc: "Filter by account address" },
          { name: "from",     type: "unix ts", required: false, desc: "Start timestamp filter" },
          { name: "to",       type: "unix ts", required: false, desc: "End timestamp filter" },
          { name: "page",     type: "number",  required: false, desc: "Page number (default: 1)" },
          { name: "pageSize", type: "number",  required: false, desc: "Results per page (default: 50, max: 200)" },
        ],
        curlExample: `curl "${BASE}/api/trades/swaps?account=0xYOUR_ADDRESS"`,
        responseExample: `{
  "total": 12,
  "page": 1,
  "pageSize": 50,
  "items": [
    {
      "id": 382,
      "account": "0x1234...abcd",
      "token_in": "0xb54a...a4b",
      "token_out": "0x7130...d9c",
      "amount_in": "1000000000",
      "amount_out": "14754098",
      "block_ts": 1700035100
    }
  ]
}`,
      },
    ],
  },

  // ── Account ─────────────────────────────────────────────────────────────────
  {
    id: "account",
    label: "Account",
    icon: "👤",
    desc: "Per-account stats, PnL history, and positions",
    endpoints: [
      {
        id: "account-stats",
        method: "GET",
        path: "/api/account/:address/stats",
        title: "Account Stats",
        desc: "Returns lifetime trading statistics for an address — total trades, volume, win rate, PnL, and average leverage.",
        params: [
          { name: "address", type: "string (path)", required: true, desc: "Trader address (lowercase)" },
        ],
        curlExample: `curl ${BASE}/api/account/0xYOUR_ADDRESS/stats`,
        responseExample: `{
  "account": "0x1234...abcd",
  "totalTrades": 142,
  "wins": 89,
  "losses": 53,
  "winRate": 62.68,
  "volumeUsd": "14200000000000000000000000000000000000",
  "realisedPnl": "820000000000000000000000000000000",
  "totalFeesPaid": "142000000000000000000000000000000",
  "avgLeverage": 18.4
}`,
        auth: false,
      },
      {
        id: "account-pnl-history",
        method: "GET",
        path: "/api/account/:address/pnl-history",
        title: "PnL History",
        desc: "Returns time-bucketed PnL data for charts — daily, weekly, or monthly buckets with cumulative running total.",
        params: [
          { name: "address", type: "string (path)", required: true,  desc: "Trader address" },
          { name: "buckets", type: "number",        required: false, desc: "Number of buckets to return (default: 30, max: 365)" },
          { name: "period",  type: "string",        required: false, desc: "Bucket size: day | week | month (default: day)" },
        ],
        curlExample: `curl "${BASE}/api/account/0xYOUR_ADDRESS/pnl-history?buckets=30&period=day"`,
        responseExample: `{
  "account": "0x1234...abcd",
  "period": "day",
  "buckets": [
    {
      "bucketTs": 1699920000,
      "pnlUsd": "120000000000000000000000000000000",
      "cumPnlUsd": "120000000000000000000000000000000",
      "volumeUsd": "5000000000000000000000000000000000",
      "tradeCount": 8,
      "wins": 6,
      "losses": 2
    }
  ]
}`,
      },
    ],
  },

  // ── Positions ───────────────────────────────────────────────────────────────
  {
    id: "positions",
    label: "Positions",
    icon: "📈",
    desc: "Open positions",
    endpoints: [
      {
        id: "positions-open",
        method: "GET",
        path: "/api/positions/open",
        title: "Open Positions",
        desc: "Returns all currently open positions. Filter by account or index token.",
        params: [
          { name: "account",    type: "string", required: false, desc: "Filter by trader address" },
          { name: "indexToken", type: "string", required: false, desc: "Filter by market (index token address)" },
        ],
        curlExample: `curl "${BASE}/api/positions/open?account=0xYOUR_ADDRESS"`,
        responseExample: `[
  {
    "account": "0x1234...abcd",
    "index_token": "0x7130...d9c",
    "collateral_token": "0xb54a...a4b",
    "size": "50000000000000000000000000000000000",
    "collateral": "1000000000000000000000000000000000",
    "is_long": true,
    "entry_price": "67842500000000000000000000000000000",
    "last_updated_ts": 1700035248
  }
]`,
      },
    ],
  },

  // ── Orders ──────────────────────────────────────────────────────────────────
  {
    id: "orders",
    label: "Orders",
    icon: "📋",
    desc: "Pending limit and trigger orders",
    endpoints: [
      {
        id: "orders-list",
        method: "GET",
        path: "/api/orders",
        title: "All Orders",
        desc: "Returns all pending orders across all accounts.",
        curlExample: `curl ${BASE}/api/orders`,
        responseExample: `{
  "total": 42,
  "page": 1,
  "pageSize": 50,
  "items": [
    {
      "account": "0x1234...abcd",
      "index_token": "0x7130...d9c",
      "is_long": true,
      "order_type": "limit",
      "trigger_price": "65000000000000000000000000000000000",
      "size_delta": "10000000000000000000000000000000000",
      "created_ts": 1700035100
    }
  ]
}`,
      },
      {
        id: "orders-account",
        method: "GET",
        path: "/api/orders/:account",
        title: "Account Orders",
        desc: "Returns all pending orders for a specific account address.",
        params: [
          { name: "account", type: "string (path)", required: true, desc: "Trader address (lowercase)" },
        ],
        curlExample: `curl ${BASE}/api/orders/0xYOUR_ADDRESS`,
        responseExample: `[
  {
    "account": "0x1234...abcd",
    "index_token": "0x7130...d9c",
    "is_long": false,
    "order_type": "stop",
    "trigger_price": "60000000000000000000000000000000000",
    "size_delta": "10000000000000000000000000000000000"
  }
]`,
      },
    ],
  },

  // ── Stats ────────────────────────────────────────────────────────────────────
  {
    id: "stats",
    label: "Stats",
    icon: "📉",
    desc: "Protocol-wide statistics and analytics",
    endpoints: [
      {
        id: "stats-fees",
        method: "GET",
        path: "/api/stats/fees",
        title: "Fee Stats",
        desc: "Returns cumulative fee revenue over time, bucketed by day.",
        params: [
          { name: "from",    type: "unix ts", required: false, desc: "Start timestamp" },
          { name: "to",      type: "unix ts", required: false, desc: "End timestamp" },
          { name: "period",  type: "string",  required: false, desc: "day | week | month (default: day)" },
        ],
        curlExample: `curl "${BASE}/api/stats/fees?period=day"`,
        responseExample: `{
  "totalFees": "2840000000000000000000000000000000",
  "buckets": [
    { "ts": 1699920000, "fees": "142000000000000000000000000000000" }
  ]
}`,
      },
      {
        id: "stats-volume",
        method: "GET",
        path: "/api/stats/volume",
        title: "Volume Stats",
        desc: "Returns trading volume over time, bucketed by period.",
        params: [
          { name: "from",   type: "unix ts", required: false, desc: "Start timestamp" },
          { name: "to",     type: "unix ts", required: false, desc: "End timestamp" },
          { name: "period", type: "string",  required: false, desc: "day | week | month (default: day)" },
        ],
        curlExample: `curl "${BASE}/api/stats/volume?period=day"`,
        responseExample: `{
  "totalVolume": "982000000000000000000000000000000000",
  "buckets": [
    { "ts": 1699920000, "volume": "42000000000000000000000000000000000" }
  ]
}`,
      },
      {
        id: "stats-users",
        method: "GET",
        path: "/api/stats/users",
        title: "User Growth",
        desc: "Returns unique trader counts over time.",
        params: [
          { name: "period", type: "string", required: false, desc: "day | week | month (default: day)" },
        ],
        curlExample: `curl "${BASE}/api/stats/users?period=week"`,
        responseExample: `{
  "totalUsers": 1842,
  "buckets": [
    { "ts": 1699920000, "newUsers": 28, "cumUsers": 1842 }
  ]
}`,
      },
      {
        id: "stats-markets",
        method: "GET",
        path: "/api/stats/markets",
        title: "Market Stats",
        desc: "Returns per-market stats: open interest, volume, long/short ratio.",
        curlExample: `curl ${BASE}/api/stats/markets`,
        responseExample: `[
  {
    "indexToken": "0x7130...d9c",
    "symbol": "BTC",
    "longOI": "1820000000000000000000000000000000000",
    "shortOI": "980000000000000000000000000000000000",
    "totalVolume": "48200000000000000000000000000000000000",
    "longRatio": 65.0
  }
]`,
      },
      {
        id: "stats-pool",
        method: "GET",
        path: "/api/stats/pool",
        title: "Pool Stats",
        desc: "Returns MLP pool composition, total AUM, and fee distribution history.",
        curlExample: `curl ${BASE}/api/stats/pool`,
        responseExample: `{
  "aumUsd": "4820000000000000000000000000000000000",
  "mlpSupply": "4200000000000000000000",
  "mlpPrice": "1148000000000000000000000000000000",
  "composition": [
    { "token": "BUSDC", "weightBps": 4800, "amountUsd": "2314000000000000000000000000000000000" },
    { "token": "BTC",   "weightBps": 2500, "amountUsd": "1205000000000000000000000000000000000" }
  ]
}`,
      },
      {
        id: "stats-liquidations",
        method: "GET",
        path: "/api/stats/liquidations",
        title: "Liquidations",
        desc: "Returns recent liquidation events with size and price.",
        params: [
          { name: "from",     type: "unix ts", required: false, desc: "Start timestamp" },
          { name: "limit",    type: "number",  required: false, desc: "Max results (default: 100)" },
        ],
        curlExample: `curl "${BASE}/api/stats/liquidations?limit=20"`,
        responseExample: `[
  {
    "account": "0x1234...abcd",
    "indexToken": "0x7130...d9c",
    "size": "200000000000000000000000000000000000",
    "isLong": true,
    "price": "62000000000000000000000000000000000",
    "blockTs": 1700034900
  }
]`,
      },
      {
        id: "stats-leaderboard",
        method: "GET",
        path: "/api/leaderboard",
        title: "Trading Leaderboard",
        desc: "Returns top traders ranked by realised PnL.",
        params: [
          { name: "limit",  type: "number", required: false, desc: "Max results (default: 50)" },
          { name: "period", type: "string", required: false, desc: "7d | 30d | all (default: 30d)" },
        ],
        curlExample: `curl "${BASE}/api/leaderboard?limit=20&period=30d"`,
        responseExample: `[
  {
    "rank": 1,
    "account": "0x1234...abcd",
    "realisedPnl": "4200000000000000000000000000000000",
    "totalTrades": 89,
    "winRate": 74.2,
    "volumeUsd": "980000000000000000000000000000000000"
  }
]`,
      },
    ],
  },

  // ── Referrals ─────────────────────────────────────────────────────────────────
  {
    id: "referrals",
    label: "Referrals",
    icon: "🔗",
    desc: "Referral codes, affiliates, and commissions",
    endpoints: [
      {
        id: "referrals-codes",
        method: "GET",
        path: "/api/referrals/codes",
        title: "All Codes",
        desc: "Returns all registered referral codes with their owner addresses.",
        curlExample: `curl ${BASE}/api/referrals/codes`,
        responseExample: `[
  { "code": "WHALE42", "owner": "0x1234...abcd", "registeredAt": 1699920000 }
]`,
      },
      {
        id: "referrals-code",
        method: "GET",
        path: "/api/referrals/code/:code",
        title: "Lookup Code",
        desc: "Returns the owner and tier of a specific referral code.",
        params: [
          { name: "code", type: "string (path)", required: true, desc: "Referral code string" },
        ],
        curlExample: `curl ${BASE}/api/referrals/code/WHALE42`,
        responseExample: `{
  "code": "WHALE42",
  "owner": "0x1234...abcd",
  "tier": 2,
  "traderCount": 28,
  "totalVolume": "4820000000000000000000000000000000000"
}`,
      },
      {
        id: "referrals-referrer",
        method: "GET",
        path: "/api/referrals/referrer/:address",
        title: "Affiliate Stats",
        desc: "Returns all stats for a referrer: their codes, referred traders, volume, and earned commissions.",
        params: [
          { name: "address", type: "string (path)", required: true, desc: "Referrer wallet address" },
        ],
        curlExample: `curl ${BASE}/api/referrals/referrer/0xYOUR_ADDRESS`,
        responseExample: `{
  "address": "0x1234...abcd",
  "codes": ["WHALE42"],
  "tier": 2,
  "referredTraderCount": 28,
  "totalVolumeUsd": "4820000000000000000000000000000000000",
  "totalCommissionUsd": "48200000000000000000000000000000"
}`,
      },
      {
        id: "referrals-trader",
        method: "GET",
        path: "/api/referrals/trader/:address",
        title: "Trader Referral",
        desc: "Returns the referral code a trader is currently registered under and their fee discount.",
        params: [
          { name: "address", type: "string (path)", required: true, desc: "Trader wallet address" },
        ],
        curlExample: `curl ${BASE}/api/referrals/trader/0xYOUR_ADDRESS`,
        responseExample: `{
  "trader": "0x1234...abcd",
  "code": "WHALE42",
  "referrer": "0x5678...efgh",
  "discountBps": 500,
  "totalRebatesUsd": "2840000000000000000000000000000"
}`,
      },
    ],
  },

  // ── Copy Trading ─────────────────────────────────────────────────────────────
  {
    id: "copy-trading",
    label: "Copy Trading",
    icon: "🤝",
    desc: "Copy trading leaderboard, followers, and portfolio",
    endpoints: [
      {
        id: "copy-leaderboard",
        method: "GET",
        path: "/api/copy/leaderboard",
        title: "Trader Leaderboard",
        desc: "Returns all registered leader traders with their on-chain profile and trading stats.",
        params: [
          { name: "sort",     type: "string", required: false, desc: "pnl | followers | winRate | volume (default: followers)" },
          { name: "pageSize", type: "number", required: false, desc: "Results per page (default: 20, max: 100)" },
          { name: "page",     type: "number", required: false, desc: "Page number (default: 1)" },
        ],
        curlExample: `curl "${BASE}/api/copy/leaderboard?sort=pnl&pageSize=20"`,
        responseExample: `{
  "total": 42,
  "page": 1,
  "pageSize": 20,
  "traders": [
    {
      "address": "0x1234...abcd",
      "copyFeeRate": "100",
      "maxFollowers": "0",
      "followerCount": "28",
      "totalVolume": "9800000000000000000000000000000000000",
      "realisedPnl": "420000000000000000000000000000000",
      "totalTrades": 142
    }
  ]
}`,
      },
      {
        id: "copy-following",
        method: "GET",
        path: "/api/copy/following/:address",
        title: "Who You Follow",
        desc: "Returns all traders a given address is currently following, with copy settings and trader profiles.",
        params: [
          { name: "address", type: "string (path)", required: true, desc: "Follower wallet address" },
        ],
        curlExample: `curl ${BASE}/api/copy/following/0xYOUR_ADDRESS`,
        responseExample: `[
  {
    "trader": "0x1234...abcd",
    "isActive": true,
    "isPaused": false,
    "collateralPerTrade": "100000000",
    "maxTotalAllocation": "1000000000",
    "currentAllocation": "350000000",
    "copyFeeRate": "100",
    "maxFollowers": "0",
    "followerCount": "28"
  }
]`,
      },
      {
        id: "copy-trader",
        method: "GET",
        path: "/api/copy/trader/:address",
        title: "Trader Profile",
        desc: "Returns a trader's full profile — followers list, on-chain stats, and recent copy execution history.",
        params: [
          { name: "address", type: "string (path)", required: true, desc: "Trader wallet address" },
        ],
        curlExample: `curl ${BASE}/api/copy/trader/0xTRADER_ADDRESS`,
        responseExample: `{
  "address": "0x1234...abcd",
  "isRegistered": true,
  "copyFeeRate": "100",
  "maxFollowers": "0",
  "followerCount": "28",
  "followers": ["0xaaaa...1111", "0xbbbb...2222"],
  "totalVolume": "9800000000000000000000000000000000000",
  "realisedPnl": "420000000000000000000000000000000",
  "totalTrades": 142,
  "copyHistory": [
    {
      "action": "increase",
      "follower": "0xaaaa...1111",
      "indexToken": "0x7130...d9c",
      "sizeDelta": "5000000000000000000000000000000000",
      "isLong": true,
      "executionPrice": "67842500000000000000000000000000000",
      "copyFee": "100000000",
      "block": 4182920
    }
  ]
}`,
      },
      {
        id: "copy-portfolio",
        method: "GET",
        path: "/api/copy/portfolio/:address",
        title: "Your Portfolio",
        desc: "Returns an overview of a follower's copy trading portfolio — all followed traders, allocations, and totals. If the address is also a registered trader, their leader profile is included.",
        params: [
          { name: "address", type: "string (path)", required: true, desc: "Follower wallet address" },
        ],
        curlExample: `curl ${BASE}/api/copy/portfolio/0xYOUR_ADDRESS`,
        responseExample: `{
  "follower": "0x1234...abcd",
  "totalAllocation": "350000000",
  "positionCount": 2,
  "positions": [
    {
      "trader": "0x5678...efgh",
      "isActive": true,
      "isPaused": false,
      "collateralPerTrade": "100000000",
      "maxTotalAllocation": "500000000",
      "currentAllocation": "200000000"
    }
  ],
  "traderProfile": null
}`,
      },
    ],
  },

  // ── API Keys ──────────────────────────────────────────────────────────────────
  {
    id: "api-keys",
    label: "API Keys",
    icon: "🔑",
    desc: "Create, list, and revoke API keys for authenticated endpoints",
    endpoints: [
      {
        id: "keys-list",
        method: "GET",
        path: "/api/keys",
        title: "List Keys",
        desc: "Returns all API keys registered for an address. Requires wallet ownership proof.",
        params: [
          { name: "address", type: "string", required: true, desc: "Wallet address to list keys for" },
        ],
        curlExample: `curl "${BASE}/api/keys?address=0xYOUR_ADDRESS"`,
        responseExample: `{
  "keys": [
    {
      "id": "key_abc123",
      "label": "Trading bot",
      "created_at": 1700000000,
      "last_used_at": 1700035248
    }
  ]
}`,
      },
      {
        id: "keys-create",
        method: "POST",
        path: "/api/keys",
        title: "Create Key",
        desc: "Creates a new API key. You submit the SHA-256 hash of the key — the raw key is generated client-side and never sent to the server. Sign the ownership proof message with your wallet.",
        bodyParams: [
          { name: "address",   type: "string", required: true,  desc: "Your wallet address (lowercase)" },
          { name: "label",     type: "string", required: false, desc: "Human-readable label (max 64 chars)" },
          { name: "key_hash",  type: "string", required: true,  desc: "SHA-256 hex hash of your raw key" },
          { name: "sig",       type: "string", required: true,  desc: "EIP-191 signature of the proof message" },
          { name: "timestamp", type: "number", required: true,  desc: "Unix timestamp used in the proof message" },
        ],
        curlExample: `# Proof message: "MoneyX API Key: <timestamp>"
# Sign it with your wallet, then POST:
curl -X POST ${BASE}/api/keys \\
  -H "Content-Type: application/json" \\
  -d '{
    "address": "0xYOUR_ADDRESS",
    "label": "Trading bot",
    "key_hash": "<sha256_of_your_raw_key>",
    "sig": "0xSIGNATURE",
    "timestamp": 1700035248
  }'`,
        responseExample: `{
  "id": "key_abc123",
  "label": "Trading bot",
  "created_at": 1700035248
}`,
      },
      {
        id: "keys-delete",
        method: "DELETE",
        path: "/api/keys/:id",
        title: "Revoke Key",
        desc: "Permanently deletes an API key. Requires a fresh ownership proof signature.",
        params: [
          { name: "id", type: "string (path)", required: true, desc: "Key ID to revoke" },
        ],
        bodyParams: [
          { name: "address",   type: "string", required: true, desc: "Your wallet address" },
          { name: "sig",       type: "string", required: true, desc: "EIP-191 signature of proof message" },
          { name: "timestamp", type: "number", required: true, desc: "Unix timestamp used in proof" },
        ],
        curlExample: `curl -X DELETE ${BASE}/api/keys/key_abc123 \\
  -H "Content-Type: application/json" \\
  -d '{
    "address": "0xYOUR_ADDRESS",
    "sig": "0xSIGNATURE",
    "timestamp": 1700035248
  }'`,
        responseExample: `{ "ok": true }`,
      },
    ],
  },
];

// ─── Method badge ─────────────────────────────────────────────────────────────

function MethodBadge({ method }: { method: Method }) {
  return (
    <span className={`AR-method AR-method--${method.toLowerCase()}`}>{method}</span>
  );
}

// ─── Endpoint card ────────────────────────────────────────────────────────────

function ParamTable({ params, title }: { params: Param[]; title: string }) {
  return (
    <div className="AR-param-block">
      <div className="AR-param-title">{title}</div>
      <table className="AR-param-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={p.name}>
              <td><code className="AR-code-inline">{p.name}</code></td>
              <td className="AR-param-type">{p.type}</td>
              <td className="AR-param-req">
                {p.required ? <span className="AR-req-yes">Yes</span> : <span className="AR-req-no">No</span>}
              </td>
              <td>{p.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [text]);
  return (
    <button className="AR-copy-btn" onClick={copy} title="Copy to clipboard">
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

function EndpointCard({ ep }: { ep: Endpoint }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="AR-endpoint" id={ep.id}>
      <button className="AR-endpoint-header" onClick={() => setOpen((o) => !o)}>
        <div className="AR-endpoint-title-row">
          <MethodBadge method={ep.method} />
          <code className="AR-endpoint-path">{ep.path}</code>
          <span className="AR-endpoint-summary">{ep.title}</span>
        </div>
        <span className="AR-chevron">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="AR-endpoint-body">
          <p className="AR-desc">{ep.desc}</p>

          {ep.params && ep.params.length > 0 && (
            <ParamTable params={ep.params} title="Parameters" />
          )}
          {ep.bodyParams && ep.bodyParams.length > 0 && (
            <ParamTable params={ep.bodyParams} title="Request Body" />
          )}

          {ep.curlExample && (
            <div className="AR-code-block-wrap">
              <div className="AR-code-block-header">
                <span>Request</span>
                <CopyButton text={ep.curlExample} />
              </div>
              <pre className="AR-code-block">{ep.curlExample}</pre>
            </div>
          )}

          {ep.responseExample && (
            <div className="AR-code-block-wrap">
              <div className="AR-code-block-header">
                <span>Response</span>
                <CopyButton text={ep.responseExample} />
              </div>
              <pre className="AR-code-block">{ep.responseExample}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Getting Started content ──────────────────────────────────────────────────

function GettingStartedContent() {
  return (
    <div className="AR-intro">
      <h1 className="AR-section-heading">API Reference</h1>
      <p className="AR-section-lead">
        The MoneyX REST API gives programmatic access to real-time prices, trade history, account stats,
        open positions, and more. All endpoints are public and read-only except API key management,
        which requires a wallet signature for ownership proof.
      </p>

      <div className="AR-intro-card AR-intro-card--url">
        <div className="AR-intro-card-label">Base URL</div>
        <div className="AR-intro-card-value">
          <code>{BASE}</code>
        </div>
      </div>

      <div className="AR-intro-grid">
        <div className="AR-intro-card">
          <div className="AR-intro-card-label">Authentication</div>
          <div className="AR-intro-card-body">
            Most endpoints are <strong>public</strong> — no key required. For authenticated endpoints, include
            your API key as the <code className="AR-code-inline">X-Api-Key</code> header.
          </div>
        </div>
        <div className="AR-intro-card">
          <div className="AR-intro-card-label">Data formats</div>
          <div className="AR-intro-card-body">
            Prices and sizes are returned as <strong>1e30 fixed-point strings</strong> (GMX convention).
            Divide by <code className="AR-code-inline">1e30</code> to get USD values.
            Timestamps are Unix seconds.
          </div>
        </div>
        <div className="AR-intro-card">
          <div className="AR-intro-card-label">Rate limits</div>
          <div className="AR-intro-card-body">
            <strong>300 requests / minute</strong> per IP. The WebSocket endpoint has no rate limit —
            use it for real-time data instead of polling REST.
          </div>
        </div>
        <div className="AR-intro-card">
          <div className="AR-intro-card-label">CORS</div>
          <div className="AR-intro-card-body">
            All endpoints support <code className="AR-code-inline">Access-Control-Allow-Origin: *</code>.
            You can call them directly from browser JavaScript.
          </div>
        </div>
      </div>

      <div className="AR-intro-section">
        <h2>Quick Start</h2>
        <div className="AR-code-block-wrap">
          <div className="AR-code-block-header">
            <span>Fetch current BTC price</span>
            <CopyButton text={`curl ${BASE}/api/prices/live | python3 -m json.tool`} />
          </div>
          <pre className="AR-code-block">{`curl ${BASE}/api/prices/live | python3 -m json.tool`}</pre>
        </div>

        <div className="AR-code-block-wrap">
          <div className="AR-code-block-header">
            <span>Get account stats (JavaScript)</span>
          </div>
          <pre className="AR-code-block">{`const address = '0xYOUR_WALLET_ADDRESS';
const res = await fetch(\`${BASE}/api/account/\${address}/stats\`);
const stats = await res.json();
console.log(\`Win rate: \${stats.winRate.toFixed(1)}%\`);
console.log(\`Total PnL: $\${(Number(stats.realisedPnl) / 1e30).toFixed(2)}\`);`}</pre>
        </div>

        <div className="AR-code-block-wrap">
          <div className="AR-code-block-header">
            <span>Subscribe to live prices (WebSocket)</span>
          </div>
          <pre className="AR-code-block">{`const ws = new WebSocket('wss://api.moneyxpro.com/ws/prices');

ws.onmessage = (event) => {
  const msg = JSON.parse(event.data);
  if (msg.type === 'snapshot') {
    console.log('All prices:', msg.data);
  } else if (msg.type === 'tick') {
    console.log(\`\${msg.data.symbol}: $\${msg.data.price}\`);
  }
};`}</pre>
        </div>
      </div>

      <div className="AR-intro-section">
        <h2>Token Addresses — BESC Hyperchain</h2>
        <div className="AR-addr-grid">
          {[
            ["WBESC", "0x33e22f85cc1877697773ca5c85988663388883a0"],
            ["BUSDC", "0xb54ad626e127f0f228dbeab6f2a61e8e6e029a4b"],
            ["MONEY", "0xf794e0b0c0a91099ccacac296193e04fb860442a"],
            ["BTC (synth)", "0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c"],
            ["ETH (synth)", "0x2170ed0880ac9a755fd29b2688956bd959f933f8"],
            ["BNB (synth)", "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"],
            ["SOL (synth)", "0x570a5d26f7765ecb712c0924e4de545b89fd43df"],
            ["XRP (synth)", "0x1d2f0da169ceb9fc7b3144628db156f3f6c60dbe"],
            ["DOGE (synth)", "0xba2ae424d960c26247dd6c32edc70b295c744c43"],
            ["AVAX (synth)", "0x1ce0c2827e2ef14d5c4f29a091d735a204794041"],
            ["AAPL (stock)", "0x53544f434b000000000000000000000000000001"],
            ["TSLA (stock)", "0x53544f434b000000000000000000000000000002"],
            ["NVDA (stock)", "0x53544f434b000000000000000000000000000003"],
            ["GOLD (stock)", "0x53544f434b00000000000000000000000000000a"],
          ].map(([sym, addr]) => (
            <div key={sym} className="AR-addr-row">
              <span className="AR-addr-sym">{sym}</span>
              <code className="AR-addr-val">{addr}</code>
              <CopyButton text={addr} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Section content ──────────────────────────────────────────────────────────

function SectionContent({ section }: { section: Section }) {
  if (section.id === "getting-started") {
    return <GettingStartedContent />;
  }

  return (
    <div>
      <h1 className="AR-section-heading">
        {section.icon} {section.label}
      </h1>
      <p className="AR-section-lead">{section.desc}</p>
      <div className="AR-endpoints">
        {section.endpoints.map((ep) => (
          <EndpointCard key={ep.id} ep={ep} />
        ))}
      </div>
    </div>
  );
}

// ─── Page shell ───────────────────────────────────────────────────────────────

export function ApiReferencePage() {
  const { section } = useParams<{ section?: string }>();
  const history = useHistory();
  const [activeId, setActiveId] = useState(
    SECTIONS.find((s) => s.id === section)?.id ?? "getting-started"
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = SECTIONS.find((s) => s.id === section)?.id;
    setActiveId(id ?? "getting-started");
  }, [section]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    contentRef.current?.scrollTo({ top: 0 });
  }, [activeId]);

  const navigate = (id: string) => {
    setActiveId(id);
    setMobileMenuOpen(false);
    history.replace(`/api-reference/${id}`);
  };

  const activeSection = SECTIONS.find((s) => s.id === activeId) ?? SECTIONS[0];
  const totalEndpoints = SECTIONS.reduce((sum, s) => sum + s.endpoints.length, 0);

  return (
    <SEO title={getPageTitle(`API Reference — ${activeSection.label}`)}>
      <div className="AR-page">
        <div className="AR-layout">

          {/* ── Sidebar ─────────────────────────────────────────── */}
          <aside className={`AR-sidebar ${mobileMenuOpen ? "open" : ""}`}>
            <div className="AR-sidebar-top">
              <div className="AR-sidebar-brand">
                <span className="AR-sidebar-brand-icon">⚡</span>
                <span className="AR-sidebar-brand-text">API Reference</span>
              </div>
              <div className="AR-sidebar-meta">{totalEndpoints} endpoints</div>
              <button className="AR-sidebar-close" onClick={() => setMobileMenuOpen(false)}>✕</button>
            </div>

            <nav className="AR-nav">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  className={`AR-nav-item ${activeId === s.id ? "AR-nav-item--active" : ""}`}
                  onClick={() => navigate(s.id)}
                >
                  <span className="AR-nav-icon">{s.icon}</span>
                  <span className="AR-nav-label">{s.label}</span>
                  {s.endpoints.length > 0 && (
                    <span className="AR-nav-count">{s.endpoints.length}</span>
                  )}
                </button>
              ))}
            </nav>

            <div className="AR-sidebar-footer">
              <Link to="/api-keys" className="AR-sidebar-link">🔑 Manage API Keys</Link>
              <Link to="/docs" className="AR-sidebar-link">📖 User Docs</Link>
            </div>
          </aside>

          {mobileMenuOpen && (
            <div className="AR-sidebar-backdrop" onClick={() => setMobileMenuOpen(false)} />
          )}

          {/* ── Main ─────────────────────────────────────────────── */}
          <main className="AR-main" ref={contentRef}>
            <button className="AR-mobile-menu-btn" onClick={() => setMobileMenuOpen(true)}>
              <span>{activeSection.icon} {activeSection.label}</span>
              <span className="AR-mobile-menu-arrow">☰</span>
            </button>

            <div className="AR-content">
              <SectionContent section={activeSection} />
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </SEO>
  );
}
